import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { getApiError } from '../../lib/utils';
import AdminLayout from '../../components/layouts/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Search, Users, Building2, Mail, Phone, CheckCircle, XCircle, UserPlus, X, ChevronDown, ChevronUp, Pencil, Save, User, Receipt, ShieldCheck, ShieldX, Clock, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSuperAdmin = user?.role === 'super_admin';
  const [customers, setCustomers] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [assignModal, setAssignModal] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState([]);
  const [saving, setSaving] = useState(false);
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [customerStaff, setCustomerStaff] = useState({});
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ company_name: '', billing_contact: '', email: '', phone: '', company_address: '', company_city: '', prefix: '' });
  const [addSaving, setAddSaving] = useState(false);

  useEffect(() => {
    fetchCustomers();
    fetchStaff();
  }, [search, statusFilter]);

  const fetchCustomers = async () => {
    try {
      let url = `${API_URL}/api/customers?`;
      if (search) url += `search=${encodeURIComponent(search)}&`;
      if (statusFilter !== 'all') url += `status=${statusFilter}`;
      const response = await axios.get(url);
      setCustomers(response.data.customers);
    } catch (err) {
      console.error('Error fetching customers:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStaff = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/staff/list`);
      setStaffList(response.data.staff);
    } catch (err) {
      console.error('Error fetching staff:', err);
    }
  };

  const fetchCustomerStaff = async (customerId) => {
    try {
      const response = await axios.get(`${API_URL}/api/customers/${customerId}/staff`);
      setCustomerStaff(prev => ({ ...prev, [customerId]: response.data.staff }));
    } catch (err) {
      console.error('Error fetching customer staff:', err);
    }
  };

  const toggleExpand = (customerId) => {
    if (expandedCustomer === customerId) {
      setExpandedCustomer(null);
    } else {
      setExpandedCustomer(customerId);
      if (!customerStaff[customerId]) {
        fetchCustomerStaff(customerId);
      }
    }
  };

  const openAssignModal = (customer) => {
    setAssignModal(customer);
    setSelectedStaff(customer.assigned_staff || []);
  };

  const handleAssign = async () => {
    if (selectedStaff.length < 1 || selectedStaff.length > 2) {
      toast.error('Please assign 1 or 2 staff members');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/customers/${assignModal.customer_id}/assign-staff`, {
        staff_ids: selectedStaff
      });
      toast.success('Staff assigned successfully');
      setAssignModal(null);
      fetchCustomers();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to assign staff'));
    } finally {
      setSaving(false);
    }
  };

  const toggleStaff = (staffId) => {
    if (selectedStaff.includes(staffId)) {
      setSelectedStaff(selectedStaff.filter(id => id !== staffId));
    } else if (selectedStaff.length < 2) {
      setSelectedStaff([...selectedStaff, staffId]);
    } else {
      toast.error('Maximum 2 staff members allowed');
    }
  };

  const startEdit = (customer) => {
    setExpandedCustomer(customer.customer_id);
    setEditingCustomer(customer.customer_id);
    setEditForm({
      company_name: customer.company_name || '',
      company_address: customer.company_address || customer.address || '',
      company_city: customer.company_city || '',
      email: customer.email || '',
      phone: customer.phone || '',
      prefix: customer.prefix || '',
    });
  };

  const cancelEdit = () => {
    setEditingCustomer(null);
    setEditForm({});
  };

  const saveEdit = async (customerId) => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/customers/${customerId}/edit`, editForm);
      toast.success('Customer updated successfully');
      setEditingCustomer(null);
      fetchCustomers();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to update'));
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const handleAddCustomer = async () => {
    if (!addForm.company_name) {
      toast.error('Please enter a company name');
      return;
    }
    setAddSaving(true);
    try {
      await axios.post(`${API_URL}/api/customers`, addForm);
      toast.success('Company created — customers can now sign up and select this company');
      setShowAddModal(false);
      setAddForm({ company_name: '', billing_contact: '', email: '', phone: '', company_address: '', company_city: '', prefix: '' });
      fetchCustomers();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to create company'));
    } finally {
      setAddSaving(false);
    }
  };

  const handleApproveUser = async (userId, customerId) => {
    try {
      await axios.put(`${API_URL}/api/customer-users/${userId}/approve`);
      toast.success('User approved — they can now log in');
      fetchCustomerStaff(customerId);
    } catch (err) {
      toast.error(getApiError(err, 'Failed to approve user'));
    }
  };

  const handleRejectUser = async (userId, customerId) => {
    if (!window.confirm('Are you sure you want to reject and remove this user?')) return;
    try {
      await axios.delete(`${API_URL}/api/customer-users/${userId}/reject`);
      toast.success('User rejected and removed');
      fetchCustomerStaff(customerId);
    } catch (err) {
      toast.error(getApiError(err, 'Failed to reject user'));
    }
  };

  const handleDeleteUser = async (userId, customerId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/customer-users/${userId}`);
      toast.success('User deleted');
      fetchCustomerStaff(customerId);
    } catch (err) {
      toast.error(getApiError(err, 'Failed to delete user'));
    }
  };

  return (
    <AdminLayout>
      <div className="animate-fade-in" data-testid="customer-management-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="page-title">Customer Management</h1>
            <p className="text-slate-600 mt-1">View and manage customer accounts</p>
          </div>
          <Button className="btn-primary" onClick={() => setShowAddModal(true)} data-testid="add-customer-btn">
            <UserPlus className="h-4 w-4 mr-2" />
            Add Customer
          </Button>
        </div>

        <Card className="card-elevated mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by company name, email, or account code..."
                  className="pl-10"
                  data-testid="customer-search"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40" data-testid="customer-status-filter">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardHeader className="border-b border-slate-100 pb-4">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                <Users className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <CardTitle className="card-title">Customers</CardTitle>
                <p className="text-sm text-slate-500">{customers.length} total accounts</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
              </div>
            ) : customers.length > 0 ? (
              <div className="divide-y divide-slate-100">
                {customers.map((customer) => (
                  <div key={customer.customer_id} data-testid={`customer-row-${customer.customer_id}`}>
                    {/* Main row */}
                    <div className="flex items-center px-6 py-4 hover:bg-slate-50 transition-colors">
                      <button onClick={() => toggleExpand(customer.customer_id)} className="mr-4 text-slate-400 hover:text-slate-600">
                        {expandedCustomer === customer.customer_id ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                      </button>
                      <div className="flex-1 grid grid-cols-12 gap-4 items-center">
                        <div className="col-span-3 flex items-center space-x-3">
                          <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                            <Building2 className="h-5 w-5 text-slate-500" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{customer.company_name}</p>
                            <span className="px-2 py-0.5 bg-slate-100 rounded text-xs font-mono">{customer.account_code}</span>
                            {customer.prefix && <span className="px-2 py-0.5 bg-teal-50 text-teal-700 rounded text-xs font-mono ml-1">{customer.prefix}</span>}
                          </div>
                        </div>
                        <div className="col-span-3 text-sm">
                          <div className="flex items-center text-slate-600">
                            <Mail className="h-3 w-3 mr-1.5 flex-shrink-0" />
                            {customer.email}
                          </div>
                          {customer.phone && (
                            <div className="flex items-center text-slate-500 mt-0.5">
                              <Phone className="h-3 w-3 mr-1.5 flex-shrink-0" />
                              {customer.phone}
                            </div>
                          )}
                        </div>
                        <div className="col-span-2">
                          {customer.assigned_staff_details?.length > 0 ? (
                            <div className="flex flex-col gap-1">
                              {customer.assigned_staff_details.map(s => (
                                <span key={s.user_id} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-teal-50 text-teal-700 border border-teal-100">
                                  {s.full_name}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">Unassigned</span>
                          )}
                        </div>
                        <div className="col-span-1 text-center">
                          <span className="font-semibold text-teal-700">{customer.waybill_count || 0}</span>
                          <p className="text-xs text-slate-400">waybills</p>
                          {customer.pending_users > 0 && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 mt-1" data-testid={`pending-badge-${customer.customer_id}`}>
                              {customer.pending_users} pending
                            </span>
                          )}
                        </div>
                        <div className="col-span-1">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            customer.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'
                          }`}>
                            {customer.status === 'active' ? <CheckCircle className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                            {customer.status}
                          </span>
                        </div>
                        <div className="col-span-2 flex items-center gap-2 justify-end">
                          {isSuperAdmin && (
                            <>
                              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-slate-400 hover:text-teal-600" onClick={(e) => { e.stopPropagation(); navigate(`/admin/customer/${customer.customer_id}/billing`); }} data-testid={`billing-btn-${customer.customer_id}`} title="View Billing">
                                <Receipt className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-slate-400 hover:text-teal-600" onClick={() => openAssignModal(customer)} data-testid={`assign-staff-btn-${customer.customer_id}`}>
                                <UserPlus className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-slate-400 hover:text-teal-600" onClick={() => startEdit(customer)} data-testid={`edit-customer-btn-${customer.customer_id}`}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expanded: Edit form OR Staff list */}
                    {expandedCustomer === customer.customer_id && (
                      <div className="bg-slate-50 border-t border-slate-100 px-6 py-4">
                        {editingCustomer === customer.customer_id ? (
                          <div className="max-w-2xl" data-testid="edit-customer-form">
                            <h4 className="text-sm font-semibold text-slate-700 mb-4">Edit Company Details</h4>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label className="text-xs text-slate-500">Company Name</Label>
                                <Input value={editForm.company_name} onChange={e => setEditForm({...editForm, company_name: e.target.value})} className="mt-1" data-testid="edit-cust-name" />
                              </div>
                              <div>
                                <Label className="text-xs text-slate-500">Email</Label>
                                <Input value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} className="mt-1" data-testid="edit-cust-email" />
                              </div>
                              <div>
                                <Label className="text-xs text-slate-500">Phone</Label>
                                <Input value={editForm.phone} onChange={e => setEditForm({...editForm, phone: e.target.value})} className="mt-1" data-testid="edit-cust-phone" />
                              </div>
                              <div className="col-span-2">
                                <Label className="text-xs text-slate-500">Address</Label>
                                <Input value={editForm.company_address} onChange={e => setEditForm({...editForm, company_address: e.target.value})} className="mt-1" data-testid="edit-cust-address" />
                              </div>
                              <div>
                                <Label className="text-xs text-slate-500">City</Label>
                                <Input value={editForm.company_city} onChange={e => setEditForm({...editForm, company_city: e.target.value})} className="mt-1" data-testid="edit-cust-city" />
                              </div>
                              <div>
                                <Label className="text-xs text-slate-500">Waybill Prefix (max 3 chars)</Label>
                                <Input value={editForm.prefix} onChange={e => setEditForm({...editForm, prefix: e.target.value.toUpperCase().slice(0, 3)})} className="mt-1 font-mono uppercase" maxLength={3} placeholder="e.g. NXL" data-testid="edit-cust-prefix" />
                                <p className="text-xs text-slate-400 mt-1">Waybills will be: {editForm.prefix || 'WB'}00001-OL</p>
                              </div>
                            </div>
                            <div className="flex gap-3 mt-4">
                              <Button variant="outline" size="sm" onClick={cancelEdit}>Cancel</Button>
                              <Button size="sm" className="btn-primary gap-2" onClick={() => saveEdit(customer.customer_id)} disabled={saving} data-testid="save-cust-edit-btn">
                                <Save className="h-3.5 w-3.5" /> {saving ? 'Saving...' : 'Save'}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div data-testid="customer-staff-list">
                            <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                              <User className="h-4 w-4" />
                              Registered Users ({customerStaff[customer.customer_id]?.length || 0})
                            </h4>
                            {customerStaff[customer.customer_id]?.length > 0 ? (
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {customerStaff[customer.customer_id].map(s => (
                                  <div key={s.user_id} className={`flex items-center gap-3 p-3 bg-white rounded-lg border ${s.approved === false ? 'border-amber-300 bg-amber-50/30' : 'border-slate-200'}`} data-testid={`staff-card-${s.user_id}`}>
                                    <div className={`h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0 ${s.approved === false ? 'bg-amber-50' : 'bg-teal-50'}`}>
                                      {s.approved === false ? <Clock className="h-4 w-4 text-amber-600" /> : <User className="h-4 w-4 text-teal-600" />}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <p className="text-sm font-medium text-slate-900 truncate">{s.full_name}</p>
                                      <p className="text-xs text-slate-500 truncate">{s.email}</p>
                                      {s.approved === false ? (
                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 mt-1">Pending Approval</span>
                                      ) : (
                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700 mt-1">Active</span>
                                      )}
                                    </div>
                                    {s.approved === false ? (
                                      <div className="flex gap-1 flex-shrink-0">
                                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" onClick={() => handleApproveUser(s.user_id, customer.customer_id)} data-testid={`approve-user-${s.user_id}`} title="Approve">
                                          <ShieldCheck className="h-4 w-4" />
                                        </Button>
                                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => handleRejectUser(s.user_id, customer.customer_id)} data-testid={`reject-user-${s.user_id}`} title="Reject">
                                          <ShieldX className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    ) : isSuperAdmin && (
                                      <div className="flex-shrink-0">
                                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-400 hover:text-red-600 hover:bg-red-50" onClick={() => handleDeleteUser(s.user_id, customer.customer_id, s.full_name)} data-testid={`delete-user-${s.user_id}`} title="Delete user">
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-slate-400">No users registered yet</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500">
                No customers found
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Assign Staff Modal */}
      {assignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="assign-staff-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-5 border-b border-slate-100">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Assign Staff</h3>
                <p className="text-sm text-slate-500 mt-0.5">{assignModal.company_name}</p>
              </div>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setAssignModal(null)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="p-5">
              <p className="text-sm text-slate-600 mb-4">Select 1-2 staff members to assign to this customer.</p>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {staffList.map(s => (
                  <button
                    key={s.user_id}
                    onClick={() => toggleStaff(s.user_id)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg border transition-colors text-left ${
                      selectedStaff.includes(s.user_id)
                        ? 'border-teal-300 bg-teal-50'
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                    data-testid={`staff-option-${s.user_id}`}
                  >
                    <div>
                      <p className="font-medium text-slate-900 text-sm">{s.full_name}</p>
                      <p className="text-xs text-slate-500">{s.email}</p>
                    </div>
                    {selectedStaff.includes(s.user_id) && (
                      <CheckCircle className="h-5 w-5 text-teal-600 flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-3">{selectedStaff.length}/2 selected</p>
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-slate-100">
              <Button variant="outline" onClick={() => setAssignModal(null)}>Cancel</Button>
              <Button
                onClick={handleAssign}
                disabled={saving || selectedStaff.length < 1}
                className="btn-primary"
                data-testid="confirm-assign-btn"
              >
                {saving ? 'Saving...' : 'Assign Staff'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Customer Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAddModal(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="add-customer-modal">
            <div className="flex items-center justify-between p-5 border-b border-slate-100">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Add New Company</h3>
                <p className="text-sm text-slate-500 mt-0.5">Customers will sign up themselves and select this company</p>
              </div>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setShowAddModal(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label className="text-xs text-slate-500">Company Name *</Label>
                  <Input value={addForm.company_name} onChange={e => setAddForm({...addForm, company_name: e.target.value})} placeholder="e.g. Acme Logistics" className="mt-1" data-testid="add-cust-company" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Waybill Prefix</Label>
                  <Input value={addForm.prefix} onChange={e => setAddForm({...addForm, prefix: e.target.value.toUpperCase().slice(0, 3)})} placeholder="e.g. ACM" maxLength={3} className="mt-1 uppercase" data-testid="add-cust-prefix" />
                  <p className="text-xs text-slate-400 mt-1">3-letter prefix for waybill numbers</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Billing Contact</Label>
                  <Input value={addForm.billing_contact} onChange={e => setAddForm({...addForm, billing_contact: e.target.value})} placeholder="Contact name" className="mt-1" data-testid="add-cust-billing-contact" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Email</Label>
                  <Input type="email" value={addForm.email} onChange={e => setAddForm({...addForm, email: e.target.value})} placeholder="accounts@company.co.za" className="mt-1" data-testid="add-cust-email" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Phone</Label>
                  <Input value={addForm.phone} onChange={e => setAddForm({...addForm, phone: e.target.value})} placeholder="+27..." className="mt-1" data-testid="add-cust-phone" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs text-slate-500">Company Address</Label>
                  <Input value={addForm.company_address} onChange={e => setAddForm({...addForm, company_address: e.target.value})} placeholder="Street address" className="mt-1" data-testid="add-cust-address" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs text-slate-500">City</Label>
                  <Input value={addForm.company_city} onChange={e => setAddForm({...addForm, company_city: e.target.value})} placeholder="e.g. Johannesburg" className="mt-1" data-testid="add-cust-city" />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-slate-100">
              <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
              <Button onClick={handleAddCustomer} disabled={addSaving} className="btn-primary" data-testid="confirm-add-customer-btn">
                {addSaving ? 'Creating...' : 'Create Company'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default CustomerManagement;
